"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, PhoneCall, ShieldCheck, ClipboardCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceCardItem {
  id: string;
  title: string;
  description: string;
  to: string;
  tag: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const relatedServices: ServiceCardItem[] = [{
  id: "water-restoration",
  title: "Water Damage & Flood Restoration",
  description: "Industrial extraction, targeted wall cavity drying, and rapid moisture mapping across multiple floors.",
  to: "/WaterDamageFloodRestorationPage",
  tag: "Immediate Extraction",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_water_extraction_containment.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_water_extraction_containment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_water_extraction_containment.png-wvc-sizes",
  imageAlt: "Commercial water extraction equipment deployed in hallway"
}, {
  id: "fire-restoration",
  title: "Fire, Smoke & Odor Restoration",
  description: "Soot neutralization, thermal odor remediation, and structural stabilization protecting multi-unit air quality.",
  to: "/FireSmokeOdorRestorationPage",
  tag: "Thermal Scrubber Units",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_fire_smoke_cleanup.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_fire_smoke_cleanup.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_fire_smoke_cleanup.png-wvc-sizes",
  imageAlt: "Technician inspecting smoke containment barriers"
}, {
  id: "reconstruction",
  title: "Reconstruction & Design-Build",
  description: "Single-contract rebuilds from framing to final paint, ensuring units return to occupancy without subcontractor handoffs.",
  to: "/ReconstructionDesignBuildPage",
  tag: "Licensed General Contracting",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_reconstruction_framing.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_reconstruction_framing.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_reconstruction_framing.webp-wvc-sizes",
  imageAlt: "Commercial interior rebuild and framing restoration"
}];
const pillars = [{
  title: "Direct tenant and board updates",
  text: "Your dedicated crew leader provides daily milestone summaries and unit-by-unit drying logs for property managers and HOA trustees."
}, {
  title: "Itemized commercial billing",
  text: "Line-item estimates formatted in Xactimate ready for commercial adjusters, reducing claims friction and documentation disputes."
}, {
  title: "Minimizing business interruption",
  text: "Phased containment walls and negative air pressure keep unimpacted suites, common corridors, and amenities operational."
}];
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-12 border-b border-border text-xs font-mono tracking-wide text-muted-foreground uppercase">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span>Commercial & HOA Property Division</span>
          </div>
          <div>Field Operations: Maryland • Washington DC • Northern Virginia</div>
        </div>

        {/* Narrative & Field Photo Consultation */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-20">
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUpVariant} className="lg:col-span-7 flex flex-col justify-center space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-medium self-start">
              <ShieldCheck className="w-3.5 h-3.5 text-accent" />
              <span>Commercial Account Management</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Direct project leadership for complex properties
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              When a pipe bursts across three floors or an electrical fire affects multi-family units, board members and property managers need immediate clarity. We assign a single, credentialed project manager who remains on site, directs the local crews, coordinates with your insurance adjusters, and protects your tenants from avoidable downtime.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {pillars.map((pillar, i) => <div key={pillar.title} data-index={i} className="rounded-md border border-border bg-card/60 p-4 flex flex-col justify-between">
                  <div className="font-semibold text-sm text-foreground mb-1.5 flex items-center gap-1.5">
                    <ClipboardCheck className="w-4 h-4 text-primary shrink-0" />
                    <span>{pillar.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {pillar.text}
                  </p>
                </div>)}
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
                <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted">
                <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                  <span>Schedule Site Consultation</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Consultation Photo with Technical Overlay */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUpVariant} className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-border bg-card shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_crew_leader_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_crew_leader_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_crew_leader_consultation.png-wvc-sizes" alt="TriServe crew supervisor consulting on clipboard with commercial property manager" loading="lazy" decoding="async" className="w-full h-[360px] sm:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-md bg-background/90 backdrop-blur-xs border border-border">
                <div className="flex items-center justify-between text-xs font-mono text-muted-foreground mb-1">
                  <span>ON-SITE PROTOCOL</span>
                  <span className="text-primary font-semibold">DIRECT FIELD CONTACT</span>
                </div>
                <p className="text-xs text-foreground font-medium">
                  Direct radio and phone access to the active lead supervisor. No intermediary answering services during business emergencies.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Section Transition Header for Related Services */}
        <div className="border-t border-border pt-12 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary mb-2">
              <Clock className="w-3.5 h-3.5" />
              <span>Full-Scope Capabilities</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground">
              Integrated commercial restoration services
            </h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            Commercial and multi-family structures require coordinated multi-discipline mitigation. TriServe handles every phase under one roof.
          </p>
        </div>

        {/* Three Related Services Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          {relatedServices.map((service, index) => <div key={service.id} data-index={index} className="h-full">
              <Card className="h-full bg-card border-border flex flex-col justify-between overflow-hidden group transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                <div>
                  <div className="relative h-48 w-full overflow-hidden bg-muted border-b border-border">
                    <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                    <div className="absolute top-3 left-3 bg-background/90 border border-border px-2.5 py-0.5 rounded-sm text-[11px] font-mono text-foreground font-medium">
                      {service.tag}
                    </div>
                  </div>

                  <CardHeader className="pt-5 pb-2">
                    <CardTitle className="text-lg font-bold text-foreground leading-snug">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </div>

                <CardFooter className="pt-4 pb-6">
                  <Button asChild variant="outline" size="sm" className="w-full border-border text-foreground group-hover:border-primary group-hover:text-primary transition-colors">
                    <Link to={service.to} className="flex items-center justify-between w-full">
                      <span>View service details</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}